import { useState, type FormEvent } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { siteInfo } from "@/mocks/comicData";

const FORM_URL = "https://readdy.ai/api/form/da2mmo3d3r93mn1k3c70";

type Status = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();

      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const serverMsg =
        typeof meta?.message === "string"
          ? meta.message
          : typeof parsed?.message === "string"
            ? parsed.message
            : typeof meta?.detail === "string"
              ? meta.detail
              : responseText;

      const isSpam =
        responseText.toLowerCase().includes("spam") ||
        (typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam"));

      if (!response.ok || !parsed || parsed.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后再试。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后再试。");
    }
  }

  return (
    <section id="contact" className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-2">
          <div className="flex flex-col gap-5">
            <SectionHeading
              align="left"
              eyebrow="联系方式"
              title="有想补充的角色细节？欢迎聊聊"
              description="如果你对某个人物设定或关系线有不同理解，或想投稿角色解读，欢迎填写右侧表单，我们会认真阅读每一条反馈。"
            />
            <div className="flex flex-col gap-4">
              {[
                { icon: "ri-mail-line", label: "反馈邮箱", value: "hello@stepmom-stepsis.example" },
                { icon: "ri-chat-3-line", label: "讨论社区", value: "角色解读与追更交流" },
                { icon: "ri-timer-line", label: "更新计划", value: "随连载进度持续补充" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary-500 text-background-50">
                    <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{item.label}</div>
                    <div className="text-sm font-semibold text-foreground-900">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="font-heading text-lg font-semibold text-primary-700 italic">
              「{siteInfo.tagline}」
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form="comic-contact"
            className="flex flex-col gap-4 rounded-3xl border border-background-200/80 bg-background-50 p-6 md:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-800">
                  称呼
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="怎么称呼你"
                  autoComplete="name"
                  className="rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-800">
                  邮箱
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于回复你"
                  autoComplete="email"
                  className="rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="subject" className="text-sm font-medium text-foreground-800">
                主题
              </label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="想聊的角色或关系"
                className="rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm font-medium text-foreground-800">
                内容
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="说说你对角色设定或人物关系的看法（500 字以内）"
                className="resize-none rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
              <span className="text-right text-xs text-foreground-400">最多 500 字</span>
            </div>

            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="hp-field"
            />

            {status === "success" && (
              <p className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                已收到你的反馈，感谢你愿意参与讨论这部漫画。
              </p>
            )}
            {status === "error" && (
              <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                {formError || "提交未成功，请稍后再试。"}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors"
            >
              {status === "loading" ? "提交中…" : "提交反馈"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}